import { useQueryClient } from '@tanstack/react-query';
import { useModeAwareQuery, useModeAwareMutation } from './useModeAwareQuery';
import { useApi } from './useApi';

// ── API response / request shapes ────────────────────────────────────────────

export interface EmailSettingsResponse {
  fromEmail: string;
  fromDisplayName: string;
}

export interface UpsertEmailSettingsRequest {
  fromDisplayName: string;
}

// ── Demo / mock data ─────────────────────────────────────────────────────────

const MOCK_EMAIL_SETTINGS: EmailSettingsResponse = {
  fromEmail: 'noreply@example.com',
  fromDisplayName: 'noreply@example.com',
};

// ── Query ────────────────────────────────────────────────────────────────────

export function useEmailSettings() {
  const { apiGet } = useApi();

  return useModeAwareQuery<EmailSettingsResponse, Error>(
    ['email-settings'],
    async ({ signal }) => {
      return apiGet<EmailSettingsResponse>(
        '/api/v1/settings/email',
        undefined,
        signal,
      );
    },
    MOCK_EMAIL_SETTINGS,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
}

// ── Mutation ─────────────────────────────────────────────────────────────────

export function useUpsertEmailSettings() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<EmailSettingsResponse, Error, UpsertEmailSettingsRequest>(
    async (data) => {
      return apiPut<EmailSettingsResponse>('/api/v1/settings/email', data);
    },
    async (data) => {
      return {
        fromEmail: 'noreply@example.com',
        fromDisplayName: data.fromDisplayName,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['email-settings'] });
      },
    },
  );
}
